import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { Grid, GridColumn as Column, GridCustomCellProps, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { Checkbox, CheckboxChangeEvent, NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { Link } from 'react-router-dom';
import PartAutoComplete from '../PartAutoComplete';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from 'TypeGen/json-response';
import type { WorkOrderTaskDetailViewModel, WorkOrderTaskPartViewModel } from './WorkOrderDetail';

type PartSummary = {
  PartID: number;
  PartNumber: string;
  Description?: string;
  QuantityInStock: number;
  UnitOfMeasure: string;
  AverageCost: number;
  IsActive: boolean;
};

type PartLocationOption = {
  PartLocationID: number;
  LocationName: string;
  QuantityOnHand: number;
};

type EditableTaskPart = WorkOrderTaskPartViewModel & {
  WorkOrderTaskID: number;
  inEdit?: boolean;
  isNew?: boolean;
  loadingLocations?: boolean;
  locationOptions?: PartLocationOption[];
};

type Props = {
  task: WorkOrderTaskDetailViewModel;
  onRefresh: () => void;
  disabled?: boolean;
};

type EditCommandCellProps = GridCustomCellProps & {
  edit: (item: EditableTaskPart) => void;
  remove: (item: EditableTaskPart) => void;
  add: (item: EditableTaskPart) => void;
  discard: () => void;
  update: (item: EditableTaskPart) => void;
  cancel: (item: EditableTaskPart) => void;
  editField: string;
  disabled?: boolean;
};

const formatCurrency = (value?: number | null) => {
  const amount = value ?? 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const PartDisplayCell = (props: GridCustomCellProps) => {
  if (!props.field) return null;
  const dataItem = props.dataItem as EditableTaskPart;

  return (
    <td {...props.tdProps}>
      <div className="fw-bold">
        {dataItem.IsCustomPart ? (dataItem.CustomPartDescription || 'Custom Part') : (dataItem.PartNumber || 'N/A')}
      </div>
      <div className="text-muted small">
        {dataItem.IsCustomPart ? 'Custom part' : (dataItem.PartDescription || 'No description')}
      </div>
    </td>
  );
};

const LocationCell = (props: GridCustomCellProps) => {
  if (!props.field) return null;
  const dataItem = props.dataItem as EditableTaskPart;

  const handleChange = (e: DropDownListChangeEvent) => {
    if (!props.onChange) return;
    const value = e.target.value as PartLocationOption | null;
    props.onChange({
      dataIndex: 0,
      dataItem: props.dataItem,
      field: props.field,
      syntheticEvent: e.syntheticEvent,
      value: value?.PartLocationID ?? null
    });
  };

  const locationValue = dataItem.locationOptions?.find((x) => x.PartLocationID === dataItem.PartLocationID) ?? null;

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit && !dataItem.IsCustomPart ? (
        dataItem.loadingLocations ? (
          <div className="d-flex align-items-center">
            <Loader type="infinite-spinner" />
            <span className="ms-2">Loading...</span>
          </div>
        ) : dataItem.locationOptions && dataItem.locationOptions.length > 0 ? (
          <DropDownList
            data={dataItem.locationOptions}
            textField="LocationName"
            dataItemKey="PartLocationID"
            value={locationValue}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        ) : (
          <span className="text-muted">N/A</span>
        )
      ) : (
        <span>{dataItem.LocationName || 'N/A'}</span>
      )}
    </td>
  );
};

const QuantityCell = (props: GridCustomCellProps) => {
  if (!props.field) return null;
  const dataItem = props.dataItem as EditableTaskPart;

  const handleChange = (e: any) => {
    if (!props.onChange) return;
    props.onChange({
      dataIndex: 0,
      dataItem: props.dataItem,
      field: props.field,
      syntheticEvent: e.syntheticEvent,
      value: e.value
    });
  };

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? (
        <NumericTextBox
          value={dataItem.QuantityUsed}
          onChange={handleChange}
          min={1}
          step={1}
          format="n0"
          style={{ width: '100%' }}
        />
      ) : (
        <span>{dataItem.QuantityUsed}</span>
      )}
    </td>
  );
};

const CostCell = (props: GridCustomCellProps) => {
  if (!props.field) return null;
  const dataItem = props.dataItem as EditableTaskPart;

  const handleChange = (e: any) => {
    if (!props.onChange) return;
    props.onChange({
      dataIndex: 0,
      dataItem: props.dataItem,
      field: props.field,
      syntheticEvent: e.syntheticEvent,
      value: e.value
    });
  };

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? (
        <NumericTextBox
          value={dataItem.CostAtTime}
          onChange={handleChange}
          min={0}
          step={0.01}
          format="c2"
          style={{ width: '100%' }}
        />
      ) : (
        <span>{formatCurrency(dataItem.CostAtTime)}</span>
      )}
    </td>
  );
};

const PurchaseOrderCell = (props: GridCustomCellProps) => {
  if (!props.field) return null;
  const dataItem = props.dataItem as EditableTaskPart;

  const handleChange = (e: any) => {
    if (!props.onChange) return;
    props.onChange({
      dataIndex: 0,
      dataItem: props.dataItem,
      field: props.field,
      syntheticEvent: e.syntheticEvent,
      value: e.value
    });
  };

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? (
        <NumericTextBox
          value={dataItem.PurchaseOrderNumber}
          onChange={handleChange}
          min={1}
          step={1}
          format="0"
          spinners={false}
          style={{ width: '100%' }}
        />
      ) : (
        dataItem.PurchaseOrderNumber ? <Link style={{ color: "#007bff" }} to={`/PurchaseOrder/Index/${dataItem.PurchaseOrderNumber}`}>{dataItem.PurchaseOrderNumber}</Link> : <span>N/A</span>
      )}
    </td>
  );
};

const EditCommandCell = (props: EditCommandCellProps) => {
  const inEdit = Boolean((props.dataItem as EditableTaskPart).inEdit);
  const dataItem = props.dataItem as EditableTaskPart;
  const isNewItem = Boolean(dataItem.isNew);

  return inEdit ? (
    <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
      <Button
        themeColor="primary"
        disabled={props.disabled}
        onClick={() => isNewItem ? props.add(dataItem) : props.update(dataItem)}
      >
        Save
      </Button>
      <Button
        themeColor="secondary"
        onClick={() => isNewItem ? props.discard() : props.cancel(dataItem)}
      >
        {isNewItem ? 'Discard' : 'Cancel'}
      </Button>
    </td>
  ) : (
    <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
      <Button
        themeColor="primary"
        disabled={props.disabled}
        onClick={() => props.edit(dataItem)}
      >
        Edit
      </Button>
      <Button
        themeColor="secondary"
        disabled={props.disabled}
        onClick={() => props.remove(dataItem)}
      >
        Delete
      </Button>
    </td>
  );
};

const buildEditableRows = (workOrderTaskId: number, parts: WorkOrderTaskPartViewModel[]): EditableTaskPart[] =>
  parts.map((part) => ({ ...part, WorkOrderTaskID: workOrderTaskId }));

const WorkOrderTaskPartsGrid: React.FC<Props> = ({ task, onRefresh, disabled = false }) => {
  const editField = 'inEdit';
  const [parts, setParts] = useState<EditableTaskPart[]>(() => buildEditableRows(task.WorkOrderTaskID, task.Parts));
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasActiveEdit = useMemo(() => parts.some((part) => part.inEdit), [parts]);

  const updateRow = useCallback((taskPartId: number, updater: (row: EditableTaskPart) => EditableTaskPart) => {
    setParts((current) => current.map((row) => row.WorkOrderTaskPartID === taskPartId ? updater(row) : row));
  }, []);

  const loadPartLocations = useCallback(async (partId: number) => {
    const response = await fetchApi(`/api/ShopInventory/PartDetail?PartID=${partId}`) as {
      Locations?: PartLocationOption[];
    };

    return (response?.Locations ?? []).map((location) => ({
      PartLocationID: location.PartLocationID,
      LocationName: location.LocationName,
      QuantityOnHand: location.QuantityOnHand,
    }));
  }, []);

  const enterEdit = useCallback((dataItem: EditableTaskPart) => {
    if (disabled || saving || hasActiveEdit) {
      return;
    }

    const target = parts.find((part) => part.WorkOrderTaskPartID === dataItem.WorkOrderTaskPartID);
    if (!target) {
      return;
    }

    setParts((current) => current.map((part) =>
      part.WorkOrderTaskPartID === dataItem.WorkOrderTaskPartID ? { ...part, inEdit: true } : part
    ));
    setEdit((current) => ({ ...current, [dataItem.WorkOrderTaskPartID]: true }));

    if (!target.IsCustomPart && target.PartID) {
      setParts((current) => current.map((part) =>
        part.WorkOrderTaskPartID === dataItem.WorkOrderTaskPartID ? { ...part, loadingLocations: true } : part
      ));

      loadPartLocations(target.PartID)
        .then((locations) => {
          setParts((current) => current.map((part) => {
            if (part.WorkOrderTaskPartID !== dataItem.WorkOrderTaskPartID) return part;
            const selectedLocation = locations.find((location) => location.PartLocationID === part.PartLocationID)
              ?? locations.reduce<typeof locations[0] | null>((a, b) => !a || b.QuantityOnHand > a.QuantityOnHand ? b : a, null);
            return {
              ...part,
              loadingLocations: false,
              locationOptions: locations,
              PartLocationID: selectedLocation?.PartLocationID ?? part.PartLocationID ?? null,
              LocationName: selectedLocation?.LocationName ?? part.LocationName
            };
          }));
        })
        .catch((loadError) => {
          console.error('Failed to load part locations.', loadError);
          setParts((current) => current.map((part) =>
            part.WorkOrderTaskPartID === dataItem.WorkOrderTaskPartID
              ? { ...part, loadingLocations: false, locationOptions: [] }
              : part
          ));
        });
    }
  }, [disabled, hasActiveEdit, loadPartLocations, parts, saving]);

  const discard = useCallback(() => {
    setParts(buildEditableRows(task.WorkOrderTaskID, task.Parts));
    setEdit({});
    setError(null);
  }, [task.Parts, task.WorkOrderTaskID]);

  const cancel = useCallback((dataItem: EditableTaskPart) => {
    if (dataItem.isNew) {
      setParts((current) => current.filter((part) => part.WorkOrderTaskPartID !== dataItem.WorkOrderTaskPartID));
      return;
    }

    setParts(buildEditableRows(task.WorkOrderTaskID, task.Parts));
    setEdit({});
    setError(null);
  }, [task.Parts, task.WorkOrderTaskID]);

  const deletePart = useCallback(async (dataItem: EditableTaskPart) => {
    if (disabled || saving) {
      return;
    }

    if (dataItem.isNew) {
      setParts((current) => current.filter((part) => part.WorkOrderTaskPartID !== dataItem.WorkOrderTaskPartID));
      return;
    }

    const confirmDelete = window.confirm('Remove this part from the task?');
    if (!confirmDelete) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetchApi('/api/ShopWorkOrder/DeleteWorkOrderTaskPart', { WorkOrderTaskPartID: dataItem.WorkOrderTaskPartID }, 'POST') as JsonResponse;
      if (!response?.Success) {
        setError(response?.ErrorMessage || 'Unable to delete task part.');
        return;
      }

      onRefresh();
    } catch (err) {
      console.error('Error deleting work order task part:', err);
      setError('Unable to delete task part.');
    } finally {
      setSaving(false);
    }
  }, [disabled, onRefresh, saving]);

  const validateRow = useCallback((row: EditableTaskPart) => {
    if (row.IsCustomPart) {
      if (!row.CustomPartDescription?.trim()) {
        return 'Description is required for custom parts.';
      }
    } else if (!row.PartID) {
      return 'Please select a part.';
    }

    if (!row.QuantityUsed || row.QuantityUsed <= 0) {
      return 'Quantity must be greater than zero.';
    }

    if (row.CostAtTime === null || row.CostAtTime < 0) {
      return 'Cost cannot be negative.';
    }

    if (!row.IsCustomPart && row.locationOptions && row.locationOptions.length > 0 && !row.PartLocationID) {
      return 'Select a location for this part.';
    }

    return null;
  }, []);

  const persistRow = useCallback(async (row: EditableTaskPart) => {
    const validationError = validateRow(row);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      WorkOrderTaskID: task.WorkOrderTaskID,
      PartID: row.IsCustomPart ? null : row.PartID ?? null,
      PartLocationID: row.IsCustomPart ? null : row.PartLocationID ?? null,
      PurchaseOrderNumber: row.PurchaseOrderNumber ?? null,
      QuantityUsed: row.QuantityUsed,
      CostAtTime: row.CostAtTime,
      IsCustomPart: row.IsCustomPart,
      CustomPartDescription: row.IsCustomPart ? row.CustomPartDescription?.trim() ?? null : null
    };

    try {
      const response = await fetchApi(
        row.isNew ? '/api/ShopWorkOrder/AddWorkOrderTaskPart' : '/api/ShopWorkOrder/UpdateWorkOrderTaskPart',
        row.isNew ? payload : {
          WorkOrderTaskPartID: row.WorkOrderTaskPartID,
          QuantityUsed: row.QuantityUsed,
          CostAtTime: row.CostAtTime,
          PartLocationID: row.IsCustomPart ? null : row.PartLocationID ?? null,
          PurchaseOrderNumber: row.PurchaseOrderNumber ?? null
        },
        'POST'
      ) as JsonResponse;

      if (!response?.Success) {
        setError(response?.ErrorMessage || 'Unable to save task part.');
        return;
      }

      onRefresh();
      setEdit((current) => ({ ...current, [row.WorkOrderTaskPartID]: false }));
    } catch (err) {
      console.error('Failed to save task part.', err);
      setError('Unable to save task part.');
    } finally {
      setSaving(false);
    }
  }, [onRefresh, task.WorkOrderTaskID, validateRow]);

  const addNew = useCallback(() => {
    if (disabled || saving || hasActiveEdit) {
      return;
    }

    const newRow: EditableTaskPart = {
      WorkOrderTaskPartID: -Date.now(),
      WorkOrderTaskID: task.WorkOrderTaskID,
      QuantityUsed: 1,
      CostAtTime: 0,
      PurchaseOrderNumber: null,
      PartID: null,
      PartLocationID: null,
      LocationID: null,
      LocationName: '',
      IsPrimaryLocation: false,
      PartNumber: '',
      PartDescription: '',
      Note: null,
      IsCustomPart: true,
      CustomPartDescription: null,
      inEdit: true,
      isNew: true
    };

    setParts((current) => [newRow, ...current]);
    setEdit((current) => ({ ...current, [newRow.WorkOrderTaskPartID]: true }));
  }, [disabled, hasActiveEdit, saving, task.WorkOrderTaskID]);

  const itemChange = useCallback((event: GridItemChangeEvent) => {
    const field = event.field as keyof EditableTaskPart | undefined;
    if (!field) {
      return;
    }

    setParts((current) => current.map((item) => (
      item.WorkOrderTaskPartID === (event.dataItem as EditableTaskPart).WorkOrderTaskPartID
        ? { ...item, [field]: event.value } as EditableTaskPart
        : item
    )));
  }, []);

  const handlePartSelected = useCallback(async (taskPartId: number, part: PartSummary | null) => {
    updateRow(taskPartId, (row) => ({
      ...row,
      PartID: part?.PartID ?? null,
      PartNumber: part?.PartNumber ?? '',
      PartDescription: part?.Description ?? '',
      CostAtTime: part ? part.AverageCost : 0,
      PartLocationID: null,
      LocationID: null,
      LocationName: '',
      IsPrimaryLocation: false,
      loadingLocations: Boolean(part),
      locationOptions: part ? row.locationOptions : []
    }));

    if (!part) {
      return;
    }

    try {
      const locations = await loadPartLocations(part.PartID);
      updateRow(taskPartId, (row) => {
        const selectedLocation = locations.reduce<typeof locations[0] | null>((a, b) => !a || b.QuantityOnHand > a.QuantityOnHand ? b : a, null);
        return {
          ...row,
          loadingLocations: false,
          locationOptions: locations,
          PartLocationID: selectedLocation?.PartLocationID ?? null,
          LocationID: selectedLocation?.PartLocationID ?? null,
          LocationName: selectedLocation?.LocationName ?? '',
          IsPrimaryLocation: Boolean(selectedLocation)
        };
      });
    } catch (err) {
      console.error('Failed to load part locations.', err);
      updateRow(taskPartId, (row) => ({ ...row, loadingLocations: false, locationOptions: [] }));
      setError('Unable to load part locations.');
    }
  }, [loadPartLocations, updateRow]);

  const handleAddRow = useCallback(() => {
    addNew();
  }, [addNew]);

  const editMap = useMemo(
    () => Object.fromEntries(parts.filter((x) => x.inEdit).map((x) => [x.WorkOrderTaskPartID, true])),
    [parts]
  );

  const CommandCell = useCallback((props: GridCustomCellProps) => (
    <EditCommandCell
      {...props}
      edit={(item) => enterEdit(item)}
      remove={(item) => void deletePart(item)}
      add={(item) => void persistRow(item)}
      discard={discard}
      update={(item) => void persistRow(item)}
      cancel={cancel}
      editField={editField}
      disabled={saving || disabled}
    />
  ), [cancel, deletePart, disabled, discard, enterEdit, persistRow, saving]);

  const PartCell = useCallback((props: GridCustomCellProps) => {
    if (!props.field) return null;
    const dataItem = props.dataItem as EditableTaskPart;

    const toggleCustom = (nextUseCustom: boolean) => {
      setParts((current) => current.map((row) => (
        row.WorkOrderTaskPartID === dataItem.WorkOrderTaskPartID
          ? {
              ...row,
              IsCustomPart: nextUseCustom,
              CustomPartDescription: nextUseCustom ? (row.CustomPartDescription ?? '') : null,
              PartID: nextUseCustom ? null : row.PartID,
              PartNumber: nextUseCustom ? '' : row.PartNumber,
              PartDescription: nextUseCustom ? '' : row.PartDescription,
              PartLocationID: nextUseCustom ? null : row.PartLocationID,
              LocationID: nextUseCustom ? null : row.LocationID,
              LocationName: nextUseCustom ? '' : row.LocationName,
              IsPrimaryLocation: nextUseCustom ? false : row.IsPrimaryLocation
            }
          : row
      )));
    };

    return (
      <td {...props.tdProps}>
        {dataItem.inEdit && dataItem.isNew ? (
          <>
            <div className="input-group input-group-sm">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <Checkbox
                    value={dataItem.IsCustomPart}
                    onChange={(event: CheckboxChangeEvent) => toggleCustom(Boolean(event.value))}
                    disabled={saving}
                  />
                  <span className="ml-1">Custom</span>
                </span>
              </div>
              <div className="flex-grow-1">
                {dataItem.IsCustomPart ? (
                  <TextBox
                    value={dataItem.CustomPartDescription ?? ''}
                    maxLength={200}
                    disabled={saving}
                    onChange={(e) => updateRow(dataItem.WorkOrderTaskPartID, (row) => ({ ...row, CustomPartDescription: String(e.target.value ?? '') }))}
                    placeholder="Custom part description"
                  />
                ) : (
                  <PartAutoComplete
                    setFocus
                    onlyActiveParts
                    selectedPartID={dataItem.PartID ?? undefined}
                    onSelectedPart={(part) => void handlePartSelected(dataItem.WorkOrderTaskPartID, part as PartSummary | null)}
                    disabled={saving}
                    style={{ width: '100%' }}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <span>{dataItem.IsCustomPart ? (dataItem.CustomPartDescription || 'Custom Part') : (dataItem.PartNumber || 'N/A')}</span>
        )}
      </td>
    );
  }, [handlePartSelected, saving, updateRow]);

  const DescriptionCell = useCallback((props: GridCustomCellProps) => {
    if (!props.field) return null;
    const dataItem = props.dataItem as EditableTaskPart;

    if (dataItem.IsCustomPart) {
      return <td {...props.tdProps}><span className="text-muted">—</span></td>;
    }

    return (
      <td {...props.tdProps}>
        {dataItem.PartDescription ? <span>{dataItem.PartDescription}</span> : <span className="text-muted">—</span>}
      </td>
    );
  }, []);

  const rows = parts;

  return (
    <>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <Grid
        data={rows}
        dataItemKey="WorkOrderTaskPartID"
        editable={true}
        edit={editMap}
        onItemChange={itemChange}
        onRowDoubleClick={(e: GridRowDoubleClickEvent) => enterEdit(e.dataItem as EditableTaskPart)}
        showLoader={saving}
        sortable={false}
        pageable={false}
      >
        <GridToolbar>
          <Button themeColor="primary" onClick={handleAddRow} disabled={disabled || saving || hasActiveEdit}>
            Add Part
          </Button>
        </GridToolbar>
        <Column field="PartNumber" title="Part" cells={{ data: PartCell }} />
        <Column field="PartDescription" title="Description" cells={{ data: DescriptionCell }} />
        <Column field="LocationName" title="Location" cells={{ data: LocationCell }} />
        <Column field="QuantityUsed" title="Qty Used" cells={{ data: QuantityCell }} width={100} />
        <Column field="CostAtTime" title="Cost At Time" cells={{ data: CostCell }} width={120} />
        <Column field="PurchaseOrderNumber" title="Purchase Order" cells={{ data: PurchaseOrderCell }} width={100} />
        <Column cells={{ data: CommandCell }} width={180} />
      </Grid>
    </>
  );
};

export default WorkOrderTaskPartsGrid;
